import Alertize from 'app/shared/Alfacom/Alert/Alertize'
import Loading from 'app/shared/Alfacom/Loading/Loading'
import {VALIDATION, VALIDATION_ERROR} from 'app/shared/Common/Constants'
import {GetFormikValidClass} from 'app/shared/Common/Util'
import FormikErrors from 'app/shared/FormikErrors/FormikErrors'
import axios from 'axios'
import {useFormik} from 'formik'
import React, {useEffect, useState} from 'react'
import {Button, Col, Form, Image, Row} from 'react-bootstrap'
import {Link, useNavigate, useSearchParams} from 'react-router-dom'
import * as yup from 'yup'
import {PasswordMeterComponent} from '_metronic/assets/ts/components'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {confirmEmail, verifyConfirmEmail} from '../core/_requests'
import YupPassword from 'yup-password'
YupPassword(yup);

const initialValues = {
  newPassword: '',
  newPasswordConfirmation: '',
  reviewTnC: false,
}

const validationSchema = yup.object().shape({
  newPassword: yup
    .string()
    .min(VALIDATION.PASSWORD.FORM.MIN, VALIDATION_ERROR.PASSWORD.MIN(VALIDATION.PASSWORD.FORM.MIN))
    .max(VALIDATION.PASSWORD.FORM.MAX, VALIDATION_ERROR.PASSWORD.MAX(VALIDATION.PASSWORD.FORM.MAX))
    .minLowercase(VALIDATION.PASSWORD.FORM.MIN_LOWERCASE, VALIDATION_ERROR.PASSWORD.MIN_LOWERCASE(VALIDATION.PASSWORD.FORM.MIN_LOWERCASE))
    .minUppercase(VALIDATION.PASSWORD.FORM.MIN_UPPERCASE, VALIDATION_ERROR.PASSWORD.MIN_UPPERCASE(VALIDATION.PASSWORD.FORM.MIN_UPPERCASE))
    .minNumbers(VALIDATION.PASSWORD.FORM.MIN_NUMBERS, VALIDATION_ERROR.PASSWORD.MIN_NUMBERS(VALIDATION.PASSWORD.FORM.MIN_NUMBERS))
    .minSymbols(VALIDATION.PASSWORD.FORM.MIN_SYMBOLS, VALIDATION_ERROR.PASSWORD.MIN_SYMBOLS(VALIDATION.PASSWORD.FORM.MIN_SYMBOLS))
    .required(VALIDATION_ERROR.PASSWORD.REQUIREDx('Password')),
  newPasswordConfirmation: yup
    .string()
    .required(VALIDATION_ERROR.PASSWORD.REQUIREDx('Password confirmation'))
    .when('newPassword', {
      is: (val: string) => (val && val.length > 0 ? true : false),
      then: yup.string().oneOf([yup.ref('newPassword')], 'Passwords do not match'),
    }),
  reviewTnC: yup
    .boolean()
    .oneOf([true],'You must agreed to the Terms and Conditions.'),
})

export function ConfirmEmail() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams() // get query params

  const [confirmationInfo, setConfirmationInfo] = useState<{userId: string; token: string} | undefined>(undefined)
  const [isTokenValid, setIsTokenValid] = useState<{valid: boolean; username: string} | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const checkValidInfo = () => {
    try {
      if (confirmationInfo === undefined) throw new Error('Your Email Confirmation Link is invalid!')
      if (!confirmationInfo.userId) throw new Error('User Id not found in query parameters!')
      if (!confirmationInfo.token) throw new Error('Token not found in query parameters!')

      return true
    } catch (error) {
      console.error(error)
      return false
    }
  }

  const tryVerifyConfirmEmail = async () => {
    try {
      if (!checkValidInfo()) throw new Error()

      await verifyConfirmEmail(Number(confirmationInfo!.userId), confirmationInfo!.token)
        .then((res) => {
          setIsTokenValid({valid: (res.length > 0 ? true : false), username: res})

          setTimeout(() => {
            PasswordMeterComponent.bootstrap()
          })
        })
        .catch((error) => {
          throw new Error('Failed to verify Email Confirmation token.')
        })
    } catch (error) {
      console.error(error)
      setError('An error has occured. Please make sure that the URL is correct, or contact the administrator if the issue persists.')
    }
  }

  const formik = useFormik({
    initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      try {
        setLoading(true)
        setSuccess(false)
        setError(undefined)

        if (!checkValidInfo()) throw new Error()

        await confirmEmail(Number(confirmationInfo!.userId), confirmationInfo!.token, values.newPassword, values.newPasswordConfirmation)
        setLoading(false)
        setSuccess(true)
      } catch (error) {
        console.error(error)
        setLoading(false)
        setSubmitting(false)

        if (axios.isAxiosError(error) && error.response) {
          setError(error.response.data.Message)
        } else {
          setError(`An error has occured. Please make sure that the URL is correct, or contact the administrator if the issue persists.`)
        }
      }
    },
  })

  useEffect(() => {
    if (searchParams) {
      const userId = searchParams.get('userId') // get user id from query params
      const token = searchParams.get('token') // get password reset token from query params

      if (userId && token) {
        setConfirmationInfo({userId, token})
      } else {
        navigate('/auth/login')
      }
    }
  }, [searchParams])

  useEffect(() => {
    if (confirmationInfo) {
      tryVerifyConfirmEmail()
    }
  }, [confirmationInfo])

  return (
    <div className='w-100 mw-450px h-100 d-flex flex-center'>
      <Row className='g-10'>
        {confirmationInfo === undefined && (
          <>
            <Col xs={12} className='text-center'>
              <div className='separator separator-content mt-4 mb-6'>
                <h2 className='text-gray-900 fs-1 text-nowrap'>Invalid Link</h2>
              </div>
            </Col>
            <Col xs={12} className='text-center'>
              <p>An error has occured. Please make sure that the URL is correct, or contact the administrator if the issue persists.</p>
            </Col>
            <Col xs={12}>
              <Form>
                <Form.Group>
                  <div className='d-flex gap-2'>
                    <Button
                      variant='light'
                      className='flex-grow-1'
                      onClick={() => {
                        navigate('/auth/login')
                      }}
                    >
                      Back to Login Page
                    </Button>
                  </div>
                </Form.Group>
              </Form>
            </Col>
          </>
        )}
        {confirmationInfo !== undefined && (
          <>
            {isTokenValid === undefined && (
              <>
                {error ? (
                  <>
                    <Col xs={12} className='text-center'>
                      <Alertize variant='danger' faIcon={'fas fa-triangle-exclamation'} description={<>{error}</>} />
                    </Col>
                    <Col xs={12} className='text-center'>
                      <div className='d-flex gap-2'>
                        <Button
                          variant='light'
                          className='flex-grow-1'
                          onClick={() => {
                            navigate('/auth/login')
                          }}
                        >
                          Back to Login Page
                        </Button>
                      </div>
                    </Col>
                  </>
                ) : (
                  <Col xs={12} className='text-center'>
                    <div className='d-flex flex-column'>
                      <div className='position-relative min-h-100px'>
                        <Loading show={!error} type='spinner' text='Verifying...' />
                      </div>
                    </div>
                  </Col>
                )}
              </>
            )}
            {isTokenValid && isTokenValid.valid === false && (
              <>
                <Col xs={12} className='text-center'>
                  <div className='separator separator-content mt-4 mb-6'>
                    <h2 className='text-gray-900 fs-1 text-nowrap'>Link Expired</h2>
                  </div>
                </Col>
                <Col xs={12} className='text-center'>
                  <p>Your email confirmation link has expired. Please contact administrator to get a new link.</p>
                </Col>
                <Col xs={12}>
                  <Form>
                    <Form.Group>
                      <div className='d-flex gap-2'>
                        <Button
                          variant='light'
                          className='flex-grow-1'
                          onClick={() => {
                            navigate('/auth/login')
                          }}
                        >
                          Back to Login Page
                        </Button>
                      </div>
                    </Form.Group>
                  </Form>
                </Col>
              </>
            )}
            {isTokenValid && isTokenValid.valid === true && (
              <>
                {success && (
                  <>
                    <Col xs={12} className='text-center'>
                      <Image className='h-100px mb-4' src={toAbsoluteUrl('/media/auth/ok_w.png')} />
                      <div className='separator separator-content mt-4 mb-6'>
                        <h2 className='text-gray-900 fs-1 text-nowrap'>Account Activated</h2>
                      </div>
                    </Col>
                    <Col xs={12} className='text-center'>
                      <p>
                        Your account has been activated successfully. You can now{' '}
                        <Link to='/auth/login' className='fw-bold'>
                          login
                        </Link>{' '}
                        using your new password.
                      </p>
                    </Col>
                  </>
                )}
                {!success && (
                  <>
                    <Col xs={12} className='text-center'>
                      <div className='separator separator-content mt-4 mb-6'>
                        <h2 className='text-gray-900 fs-1 text-nowrap'>Update Password</h2>
                      </div>
                      <span>You need to update your password to activate your account.</span>
                    </Col>
                    <Col xs={12}>
                      <Form>
                        {error && (
                          <>
                            <Alertize variant='danger' faIcon={'fas fa-triangle-exclamation'} description={<>{error}</>} />
                          </>
                        )}

                        <Form.Group>
                          <Form.Label className={`fw-bold fs-6 required`}>Username</Form.Label>
                          <Col className='d-flex flex-column gap-2'>
                            <input type='text' disabled={true} value={isTokenValid.username} autoComplete='username' className='form-control' />
                          </Col>
                        </Form.Group>

                        <Form.Group data-kt-password-meter='true'>
                          <Form.Label className={`fw-bold fs-6 required`}>New Password</Form.Label>
                          <Col className='d-flex flex-column gap-2'>
                            <Form.Control
                              {...formik.getFieldProps('newPassword')}
                              className={`${GetFormikValidClass(formik, 'newPassword')}`}
                              type='password'
                              placeholder='New Password'
                              value={formik.values.newPassword || ''}
                              disabled={formik.isSubmitting}
                              autoComplete='new-password'
                            />
                            <FormikErrors formik={formik} fieldKey={'newPassword'} />

                            <div className='d-flex align-items-center mt-3' data-kt-password-meter-control='highlight'>
                              <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
                              <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
                              <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
                              <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px'></div>
                            </div>

                            <div className='text-muted'>
                              Password must be at least {VALIDATION.PASSWORD.FORM.MIN} characters long with the combination of 
                              uppercase letters, lowercase letters, numbers, and symbols.
                            </div>
                          </Col>
                        </Form.Group>

                        <Form.Group>
                          <Form.Label className={`fw-bold fs-6 required`}>Confirm New Password</Form.Label>
                          <Col className='d-flex flex-column gap-2'>
                            <Form.Control
                              {...formik.getFieldProps('newPasswordConfirmation')}
                              className={`${GetFormikValidClass(formik, 'newPasswordConfirmation')}`}
                              type='password'
                              placeholder='New Password Confirmation'
                              value={formik.values.newPasswordConfirmation || ''}
                              disabled={formik.isSubmitting}
                              autoComplete='new-password'
                            />
                            <FormikErrors formik={formik} fieldKey={'newPasswordConfirmation'} />
                          </Col>
                        </Form.Group>

                        <Form.Group>
                          <div className='d-flex flex-column gap-2'>
                            <Form.Check type="checkbox" id="chekcbox-tnc" disabled={formik.isSubmitting}>
                                <Form.Check.Input type="checkbox" checked={formik.values.reviewTnC} onChange={() => {formik.setFieldValue('reviewTnC', !formik.values.reviewTnC)}} />
                                <Form.Check.Label>I've read and agree to the <Link to='/terms-conditions' target='_blank' rel='noreferrer'>Terms and Conditions</Link> and <Link to='/privacy-policy' target='_blank' rel='noreferrer'>Privacy Policy</Link>.</Form.Check.Label>
                            </Form.Check>
                            <FormikErrors formik={formik} fieldKey={'reviewTnC'} />
                          </div>
                        </Form.Group>

                        <Form.Group className='mt-10'>
                          <div className='d-flex gap-2'>
                            <Button
                              type='submit'
                              className='flex-grow-1'
                              onClick={(e) => {
                                e.preventDefault()
                                formik.handleSubmit()
                              }}
                              disabled={formik.isSubmitting}
                            >
                              {!loading && <span className='indicator-label'>Submit</span>}
                              {loading && (
                                <span className='indicator-progress' style={{display: 'block'}}>
                                  <span className='spinner-border spinner-border-sm align-middle me-2'></span>
                                  Please wait...
                                </span>
                              )}
                            </Button>
                          </div>
                        </Form.Group>
                      </Form>
                    </Col>
                  </>
                )}
              </>
            )}
          </>
        )}
      </Row>
    </div>
  )
}
